import React, { ReactElement, useState } from 'react'
import {
  IonList,
  IonListHeader,
  IonItem,
  IonAvatar,
  IonLabel,
  IonIcon,
  IonGrid,
  IonRow,
  IonCol,
  IonItemDivider,
  IonItemSliding,
  IonItemOption,
  IonItemOptions,
  IonChip,
  IonReorderGroup,
  ItemReorderEventDetail,
  IonReorder,
} from '@ionic/react'
import { volumeHighOutline, pulseOutline, cameraOutline, chevronForward, trash, checkmarkSharp } from 'ionicons/icons'
import { useDispatch, useSelector } from 'react-redux'

import GridHeaderRow from '../gridHeaderRow/GridHeaderRow.component'
import { IItems, IItem } from './store/grid.types'
import './Grid.component.css'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { TimelineConstants } from '../../features/timeline/store/timeline.constants'
import { getStyleForPHI, IPHIComponent } from '../phi/phi.component'
import { setProcedureLoaderItem } from '../../features/timeline/store/timeline.actions'
import { IServiceConfig } from '../../features/configurations/store/configurations.types'
import { getServiceConfigSelector } from '../../features/configurations/selectors/serviceConfig.selector'
import { truncateSecondsIfNecessary } from '../../utilities/dateTime.util'
import TimesReorderComponent, { IPopoverData } from '../../features/timeline/timesReorder/timesReorder.component'

interface GridComponentProps extends IPHIComponent {
  showGridHeader?: boolean
  showItemDivider?: boolean
  noPadding?: boolean
  itemDivider?: string
  header?: string
  items: IItems
  hasTemplate?: boolean
  children?: ReactElement
  reorder: boolean
  editedKeys: string[]
}

const Grid: React.FC<GridComponentProps> = (props: GridComponentProps) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { t } = useTranslation()

  const serviceConfigData: IServiceConfig = useSelector(getServiceConfigSelector)
  const { DISPLAY_SECONDS } = serviceConfigData
  const displaySeconds = !!DISPLAY_SECONDS

  const {
    showGridHeader,
    showItemDivider,
    noPadding,
    itemDivider,
    header,
    items,
    hasTemplate,
    children,
    isPHI,
    reorder,
    editedKeys,
  } = props

  const [popoverState, setShowPopover] = useState({ showPopover: false })
  const [popoverData, setpopoverData] = useState<IPopoverData>()

  const onRowClick = (item: IItem) => {
    dispatch({
      type: item.avatarActionType,
      payload: item,
    })
  }

  const chevronOnClick = (item: IItem) => {
    if (reorder) {
      if (isReorder(item)) {
        const selected = items.findIndex((i) => i.key === item.key)
        showTimesReorderPopover(selected, selected)
      }
      return
    }
    dispatch({ type: item.rowActionType, payload: item })
    if (item.defaultHref) {
      dispatch(setProcedureLoaderItem(true))
      history.push(item.defaultHref, { from: window.location.pathname })
    }
  }

  const showDateTimeUnknown = (item: IItem) => {
    return !item.labelHeader && !item.subHeader && item.labelHeaderFlag && item.labelHeaderFlag === 'PTA'
  }

  const showTimesReorderPopover = (from: number, to: number, event?: CustomEvent<ItemReorderEventDetail>) => {
    const selected = items[from]
    const start = from >= to ? items[to - 1] : items[to]
    const end = from > to ? items[to] : items[to + 1]
    setpopoverData({ start, end, selected, event })
    setShowPopover({ showPopover: true })
  }

  const handleReorder = (event: CustomEvent<ItemReorderEventDetail>) => {
    const fromIndex = event.detail.from
    const toIndex = event.detail.to
    showTimesReorderPopover(fromIndex, toIndex, event)
  }

  const resetPopoverState = () => {
    setShowPopover({ showPopover: false })
    setpopoverData(undefined)
  }

  const isReorder = (item: IItem) => {
    const isTimes = item.itemType === 'Times'
    const isEKGProc =
      (item.isEkgImport === 1 || item.isEkgImport === -1) &&
      (item.itemType === 'Procedures' || item.itemType === 'Medications')
    return reorder && !isTimes && !isEKGProc
  }

  const isEditedItem = (item: IItem) => {
    return editedKeys.findIndex((key) => key === item.key) !== -1
  }

  const getLabelHeader = (labelHeader: string, item: IItem) => {
    if (reorder && isEditedItem(item)) return `(${truncateSecondsIfNecessary(labelHeader, displaySeconds)})*`
    return truncateSecondsIfNecessary(labelHeader, displaySeconds)
  }

  const getLabelStyle = (item: IItem) => {
    if (reorder && isEditedItem(item)) return { color: 'var(--ion-color-primary-alt)', fontStyle: 'italic' }
    else if (reorder) return { color: 'var(--ion-color-primary-alt)' }
  }

  const timelineItem = (item: IItem, idx: number) => (
    <>
      <IonItem
        id={item.key}
        data-testid={item.key}
        className={noPadding ? 'ion-no-padding' : ''}
        key={`list_item_${idx}`}
        onClick={() => chevronOnClick(item)}>
        {item.avatarImg && (
          <IonAvatar
            data-testid={`avatar_${idx}`}
            slot={item.avatarPosition}
            onClick={() => (item.avatarActionType ? onRowClick.bind(null, item) : null)}>
            <img src={item.avatarImg} alt='avatar' />
          </IonAvatar>
        )}

        {item.iconImg && (
          <IonAvatar
            data-testid={`avatar_${idx}`}
            className='ion-text-center'
            slot={item.avatarPosition}
            onClick={() => (item.avatarActionType ? onRowClick.bind(null, item) : null)}>
            {item.iconImg === 'volumeHighOutline' && <IonIcon icon={volumeHighOutline} size='large' />}
            {item.iconImg === 'pulseOutline' && <IonIcon icon={pulseOutline} size='large' />}
            {item.iconImg === 'cameraOutline' && <IonIcon icon={cameraOutline} size='large' />}
          </IonAvatar>
        )}

        {item.grid && (
          <IonLabel className='grid-ion-label'>
            <IonGrid>
              <IonRow>
                <div id={`${item.key}_trigger`} />
                <IonCol
                  id={`${item.key}_col`}
                  className={`timeline-item-header-col${reorder ? '-edit' : ''}${displaySeconds ? '-seconds' : ''}`}>
                  {item.labelHeaderFlag && (
                    <IonChip data-testid={`labelHeaderFlag_${idx}`} className='grid-header-chip'>
                      <IonIcon className='header-checkmark' icon={checkmarkSharp} />
                      <IonLabel>{item.labelHeaderFlag}</IonLabel>
                    </IonChip>
                  )}
                  <div data-testid={`labelHeader_${idx}`}>
                    {item.labelHeader && <h2 style={getLabelStyle(item)}>{getLabelHeader(item.labelHeader, item)}</h2>}
                    {showDateTimeUnknown(item) && (
                      <span style={{ fontSize: '60%' }}>
                        <i>Date & Time</i>
                      </span>
                    )}
                  </div>
                  <div data-testid={`subHeader_${idx}`}>
                    {item.subHeader && <h4 className='grid-label-subheader'>{item.subHeader}</h4>}
                    {showDateTimeUnknown(item) && (
                      <span style={{ fontSize: '60%', display: 'block' }}>
                        <i>Unknown</i>
                      </span>
                    )}
                  </div>
                </IonCol>
                {!item.keyValues && (
                  <IonCol data-testid={`label_item_${idx}`} className='padding-start-mobile'>
                    <IonLabel data-testid={`label_${idx}`} className='ion-text-wrap ion-no-margin'>
                      {item.event && <h2 data-testid={`event_${idx}`}>{item.event}</h2>}
                      {item.eventSubHeader && (
                        <h3
                          data-private={isPHI}
                          style={getStyleForPHI(isPHI)}
                          data-testid={`eventSubHeader_${idx}`}
                          className='wrap-two-lines'
                          id='grid-event-subheader'>
                          {item.eventSubHeader}
                        </h3>
                      )}
                      {item.eventParagraph && <p data-testid={`eventParagraph_${idx}`}>{item.eventParagraph}</p>}
                    </IonLabel>
                  </IonCol>
                )}

                {item.keyValues && (
                  <IonCol size='1' className='ion-hide-md-down'>
                    {item.event && <h2>{item.event}</h2>}
                  </IonCol>
                )}

                {item.keyValues &&
                  item.keyValues.map((kv, index) => (
                    <IonCol
                      data-testid={`col_${kv.key}_${index}`}
                      sizeMd={kv.columnSize ? kv.columnSize : ''}
                      className={`${kv.hideOnMobile ? 'ion-hide-md-down' : ''} ${
                        index === 0 ? 'ion-padding-start' : ''
                      }`}
                      key={kv.key}>
                      <div>
                        <h4 data-testid={`label_${kv.key}_${index}`} className='grid-keyValue-key' id='grid-key'>
                          {kv.key}
                        </h4>
                      </div>
                      <div>
                        <h3 data-testid={`value_${kv.key}_${index}`} className='grid-keyValue-value' id='grid-value'>
                          {kv.value}
                        </h3>
                      </div>
                    </IonCol>
                  ))}
                {item.keyValues && (
                  <IonCol
                    data-private={isPHI}
                    style={getStyleForPHI(isPHI)}
                    className='ion-hide-md-down grid-paragraph-column'>
                    {item.paragraph && <p className='grid-paragraph'>{item.paragraph}</p>}
                  </IonCol>
                )}
                {!reorder && (
                  <IonCol className='ion-hide-md-up' sizeXs='1'>
                    {item.chevronText && (
                      <IonLabel className='grid-chevron-text'>
                        <h3>{t('common.buttons.More')}</h3>
                        <IonIcon
                          className='ion-float-right'
                          data-testid={`ChevronForward_${idx}`}
                          icon={chevronForward}
                          onClick={() => chevronOnClick(item)}
                        />
                      </IonLabel>
                    )}
                    {!item.chevronText && (
                      <IonIcon
                        className='ion-float-right'
                        data-testid={`ChevronForwardMobile_${idx}`}
                        icon={chevronForward}
                        onClick={() => chevronOnClick(item)}
                      />
                    )}
                  </IonCol>
                )}
                {!reorder && (
                  <IonCol size='auto' className='ion-hide-md-down grid-chevron'>
                    <IonIcon
                      className='ion-float-right'
                      data-testid={`ChevronForwardTablet_${idx}`}
                      icon={chevronForward}
                    />
                  </IonCol>
                )}
              </IonRow>
            </IonGrid>
          </IonLabel>
        )}

        {!item.grid && (
          <IonItem>
            <IonLabel className='ion-text-wrap'>
              {item.labelHeader && <h2>{getLabelHeader(item.labelHeader, item)}</h2>}
              {item.subHeader && <h3>{item.subHeader}</h3>}
              {item.paragraph && <p>{item.paragraph}</p>}
            </IonLabel>
          </IonItem>
        )}

        {hasTemplate && children && children}

        {!item.hideChevron && !item.grid && !reorder && (
          <>
            <IonLabel slot='end' className='ion-hide-md-up'>
              {item.chevronText && <h2>More</h2>}
              <IonIcon icon={chevronForward} onClick={() => chevronOnClick(item)} />
            </IonLabel>
            <IonIcon
              slot='end'
              className='ion-hide-md-down grid-chevron'
              data-testid={`ChevronForward_${idx}`}
              icon={chevronForward}
              onClick={() => chevronOnClick(item)}
            />{' '}
          </>
        )}
        {isReorder(item) && <IonReorder></IonReorder>}
      </IonItem>
    </>
  )

  return (
    <IonList className='grid-ion-list'>
      {header && <IonListHeader>{header}</IonListHeader>}

      {showGridHeader && <GridHeaderRow />}

      {showItemDivider && (
        <IonItemDivider color='light' className={itemDivider && itemDivider !== ' ' ? 'ion-padding' : ''}>
          <IonLabel className='grid-item-divider-label' color='medium'>
            {itemDivider}
          </IonLabel>
        </IonItemDivider>
      )}
      {!reorder &&
        items &&
        items.map((item: IItem, idx: number) => (
          <IonItemSliding key={item.key ? item.key : `list_item_${idx}`}>
            {timelineItem(item, idx)}
            <IonItemOptions side='end'>
              <IonItemOption
                data-testid={`delete_${idx}`}
                color='danger'
                onDrag={() => dispatch({ type: TimelineConstants.DELETE_TIMELINE_ITEM, payload: item })}
                onClick={() => dispatch({ type: TimelineConstants.DELETE_TIMELINE_ITEM, payload: item })}>
                <IonIcon slot='icon-only' icon={trash} />
              </IonItemOption>
            </IonItemOptions>
          </IonItemSliding>
        ))}
      {reorder && (
        <IonReorderGroup disabled={!reorder} onIonItemReorder={handleReorder}>
          {items && items.map((item: IItem, idx: number) => timelineItem(item, idx))}
        </IonReorderGroup>
      )}
      <TimesReorderComponent
        popoverState={popoverState}
        resetPopoverState={resetPopoverState}
        popoverData={popoverData}></TimesReorderComponent>
    </IonList>
  )
}

export default Grid
